import { formatDistanceToNow } from "date-fns";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Icon, IconButton, Modal, Typography } from "@/components/atoms";
import { SUPPORT_EMAIL } from "../AddVehicleV3/ContactSupport";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { useKyvEnabled, useOperator } from "@/hooks";
import { useGetCountryStatesQuery } from "@/redux/apis/resource/resourceApi";
import {
  useDeleteVeivoValidationMutation,
  useListVeivoValidationsQuery,
  VeivoValidationListItem,
} from "@/redux/apis/vehicle/veivoApi";
import { addToast, clsx } from "@/utils";
import { draftChecklist, furthestStep, initialStep, readDraftData, stepLabel, V3_STEP_COUNT } from "../AddVehicleV3/steps";
import { vehicleTitle } from "../AddVehicleV3/VehicleCard";

/**
 * The add-vehicle flow's drafts, at the top of My Vehicles (DEV-1904): each
 * says which of the five steps it is on, what is done and what is next, and
 * when it was saved — "Continue" resumes right there, on any device.
 *
 * Only the Veivo path's drafts (createOn "confirm"); a verification that
 * creates its own vehicle stays in KyvValidationsPanel.
 */
export const VeivoDraftRows = () => {
  const { enabled: kyvEnabled } = useKyvEnabled();
  const { country } = useOperator();
  const { data } = useListVeivoValidationsQuery("in_progress", {
    skip: !kyvEnabled,
    refetchOnMountOrArgChange: true,
  });
  const { data: statesResponse } = useGetCountryStatesQuery(country, { skip: !kyvEnabled || !country });

  const drafts = (data?.items ?? []).filter((item) => item.createOn === "confirm");
  const abbreviationOf = useMemo(() => {
    const states = new Map(statesResponse?.data.states.map((s) => [s.id, s.abbreviation ?? s.name]));
    return (stateUuid?: string | null) => (stateUuid ? states.get(stateUuid) : undefined);
  }, [statesResponse?.data.states]);

  if (!kyvEnabled || !drafts.length) return null;

  return (
    <ul className="mb-4 flex flex-col gap-2" aria-label="Vehicles being added">
      {drafts.map((item) => (
        <DraftRow key={item.validationId} item={item} state={abbreviationOf(item.stateUuid)} />
      ))}
    </ul>
  );
};

const DraftRow = ({ item, state }: { item: VeivoValidationListItem; state?: string }) => {
  const navigate = useNavigate();
  const { id: operatorId } = useOperator();
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [discard, { isLoading: discarding, error: discardError }] = useDeleteVeivoValidationMutation();

  const data = readDraftData(item.draft?.data);
  const step = Math.min(initialStep(item.status, item.draft?.step), furthestStep(item.status, data));
  const { items, summary } = draftChecklist(item.status, data);
  const plate = item.registrationNumber ?? "";
  const title = vehicleTitle(item.vehicleSummary, plate || "Vehicle");
  const savedAt = item.draft?.savedAt ?? item.updatedAt;

  const handleDiscard = () =>
    discard(item.validationId)
      .unwrap()
      .then(() => {
        setConfirmingDelete(false);
        addToast("success", "Draft discarded.");
      })
      .catch(() => {
        // Left open: the message says why.
      });

  return (
    <li className="flex flex-wrap items-center gap-4 rounded-lg border border-neutral-mid-gray bg-white p-3">
      <span className="flex h-12 w-14 shrink-0 items-center justify-center rounded-lg bg-neutral-surface-gray text-neutral-dark-gray">
        <Icon name="Car" size="md" />
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <Typography variant="action" className="text-neutral-black">
            {title}
          </Typography>
          <Typography variant="small" className="text-neutral-dark-gray">
            {[plate, state].filter(Boolean).join(" · ")}
          </Typography>
          <span className="rounded-md bg-primary-light px-2 py-0.5">
            <Typography variant="small" className="text-primary">
              Draft
            </Typography>
          </span>
        </div>
        <div className="mt-1.5 flex flex-wrap items-center gap-3">
          <span className="flex gap-1" aria-hidden>
            {items.map((checklist) => (
              <span
                key={checklist.step}
                className={clsx(
                  "h-1.5 w-7 rounded-full",
                  checklist.state === "done" ? "bg-success" : checklist.step === step ? "bg-primary" : "bg-neutral-mid-gray"
                )}
              />
            ))}
          </span>
          <Typography variant="small" className="font-semibold text-primary">
            Step {step} of {V3_STEP_COUNT} · {stepLabel(step)}
          </Typography>
          {item.status === "rejected" ? (
            // Refused: only support can let it continue (DEV-2002).
            <Typography variant="small" className="text-error">
              · Needs support — contact {SUPPORT_EMAIL}
            </Typography>
          ) : (
            <Typography variant="small" className="text-neutral-dark-gray">
              · {summary}
            </Typography>
          )}
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-3">
        {savedAt && (
          <Typography variant="small" className="text-neutral-dark-gray">
            Saved {formatDistanceToNow(new Date(savedAt), { addSuffix: true })}
          </Typography>
        )}
        <Button variant="primary" size="md" onClick={() => navigate(`/operator/${operatorId}/vehicles/my-vehicles/verify/${item.validationId}`)}>
          Continue
        </Button>
        <IconButton
          iconName="close"
          isCustomIcon
          variant="custom"
          iconSize="lg"
          className="text-neutral-dark-gray"
          aria-label={`Discard the draft for ${plate || title}`}
          onClick={() => setConfirmingDelete(true)}
        />
      </div>

      <Modal
        open={confirmingDelete}
        onClose={() => setConfirmingDelete(false)}
        variant="web-small"
        title="Discard this draft?"
        description={
          discardError
            ? getErrorMessages(discardError)[0]
            : `The draft for ${plate || title} will be removed, along with any photos already taken. You can start again from Add Vehicle.`
        }
        renderButtons={() => (
          <>
            <Button variant="tertiary" size="md" onClick={() => setConfirmingDelete(false)}>
              Keep it
            </Button>
            <Button variant="primary" size="md" disabled={discarding} onClick={handleDiscard}>
              Discard
            </Button>
          </>
        )}
      />
    </li>
  );
};
